'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { WithClassName } from 'src/utils/UtilityTypes';
import { cj } from 'src/utils/classNames';
import { Health, Open, WarningFilled } from '@velocity-exchange/icons';
import {
	BigNum,
	MarketType,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import Select from 'src/components/primitives/Select/Select';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import { TOOLTIP_TRIGGER_DECORATION_CLASSNAME } from 'src/components/primitives/Tooltip/consts';
import { getColorVar } from 'src/utils/theme/colorVars';
import useModal from 'src/components/Modals/hooks/useModal';
import healthDependentClassName from 'src/utils/healthDependentClassName';
import {
	UIMarket,
	convertMarginRatioToLeverage,
} from '@velocity-exchange/common';
import { usePositionSummary } from 'src/hooks/usePositionSummary';
import IconButton from '../primitives/IconButton/IconButton';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import { toNotional } from 'src/utils/formatting';
import Button from '../primitives/Button/Button';
import DefinitionTooltip from '../Tooltip/DefinitionTooltip/DefinitionTooltip';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';

interface MarginCalcPanelProps extends WithClassName {
	filteredMarginCalcs?: 'cross' | 'isolated';
	includeDetailsAndDepositButtons?: boolean;
}

const MarginCalcPanel = ({
	className,
	filteredMarginCalcs,
	includeDetailsAndDepositButtons = false,
}: MarginCalcPanelProps) => {
	const userClient = useDriftAccountStore(
		(s) => s.accounts[s.currentUserKey ?? '']?.client
	);
	const isUserAccountReady = useUserAccountIsReady();
	const marginInfo = useCurrentAccountMarginInfo();
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);

	const activePerpPositions = userClient?.getActivePerpPositions();

	const { show } = useModal();
	const { switchMarket } = useSwitchMarket();

	// Initialize selection based on filter
	const getInitialSelection = (): 'cross' | `iso-${number}` => {
		if (filteredMarginCalcs === 'isolated') {
			// For isolated filter, we'll set the first isolated position as default
			// This will be updated in useEffect once marginCalc is available
			return 'cross'; // Temporary, will be updated
		}
		return 'cross';
	};

	const [selection, setSelection] = useState<'cross' | `iso-${number}`>(
		getInitialSelection()
	);

	// Compute margin calculation once per render for maintenance category
	const marginCalc = useMemo(() => {
		return userClient && isUserAccountReady
			? userClient.getMarginCalculation('Maintenance')
			: undefined;
	}, [userClient, isUserAccountReady, activePerpPositions]);

	// Track previous isolated markets to detect when a NEW isolated position is opened
	const prevIsolatedMarketsRef = useRef<Set<number> | null>(null);

	// Detect when a new isolated position is opened and switch to it
	useEffect(() => {
		if (!marginCalc || !userClient) return;

		// Get current set of isolated markets with margin requirement > 0 AND non-zero size
		// (positions with size === 0 are just the intent to open, not actual positions)
		const currentIsolatedMarkets = new Set<number>();
		marginCalc.isolatedMarginCalculations.forEach((iso, marketIndex) => {
			if (iso.marginRequirement.gt(ZERO)) {
				const perpPosition = userClient.getPerpPosition(marketIndex);
				// Only include if position has non-zero size
				if (perpPosition && !perpPosition.baseAssetAmount.eq(ZERO)) {
					currentIsolatedMarkets.add(marketIndex);
				}
			}
		});

		// Skip the first run to establish baseline - don't switch on mount
		if (prevIsolatedMarketsRef.current !== null) {
			// Find newly added isolated positions
			for (const marketIndex of currentIsolatedMarkets) {
				if (!prevIsolatedMarketsRef.current.has(marketIndex)) {
					// New isolated position detected, switch dropdown selection to it
					setSelection(`iso-${marketIndex}`);
					break; // Only handle one new position at a time
				}
			}
		}

		// Update the ref for next comparison
		prevIsolatedMarketsRef.current = currentIsolatedMarkets;
	}, [marginCalc, userClient]);

	// Build dropdown options: Cross + isolated entries (from marginCalc map)
	const dropdownOptions = useMemo(() => {
		const opts: { value: string; label: string }[] = [];

		// Add cross option only if not filtering to isolated
		if (filteredMarginCalcs !== 'isolated') {
			opts.push({
				value: 'cross',
				label: 'Cross Positions',
			});
		}

		// Add isolated options only if not filtering to cross
		if (filteredMarginCalcs !== 'cross' && userClient && marginCalc) {
			// Use isolated margin calculations keys as source of isolated markets
			marginCalc.isolatedMarginCalculations.forEach((iso, marketIndex) => {
				if (iso.marginRequirement.gt(ZERO)) {
					const ui = UIMarket.createPerpMarket(marketIndex).uiSymbols;
					opts.push({
						value: `iso-${marketIndex}`,
						label: `${ui.marketDisplaySymbol} (Isolated)`,
					});
				}
			});
		}
		return opts;
	}, [marginCalc, userClient, filteredMarginCalcs]);

	// Track which isolated markets have low health for warning icon
	const lowHealthMarkets = useMemo(() => {
		const set = new Set<number>();
		if (userClient && marginCalc) {
			marginCalc.isolatedMarginCalculations.forEach((iso, marketIndex) => {
				if (iso.marginRequirement.gt(ZERO)) {
					const health = userClient.getHealth(marketIndex);
					if (health < 20) set.add(marketIndex);
				}
			});
		}
		return set;
	}, [marginCalc, userClient]);

	const hasMultipleOptions = dropdownOptions.length > 1;

	const {
		value: selectedValue,
		open,
		onOpenChange,
		onValueChange,
		selectedOption,
		selectOptions,
	} = useSelect({
		options: dropdownOptions,
		onSelectedValueChange: (val) => {
			setSelection(val as 'cross' | `iso-${number}`);
			// When selecting an isolated position, switch to that market
			if (val.startsWith('iso-')) {
				const marketIndex = Number(val.split('iso-')[1]);
				switchMarket({ marketIndex, marketType: MarketType.PERP });
			} else {
				const biggestNotionalCrossPosition = activePerpPositions
					?.filter((pos) => !userClient?.isPerpPositionIsolated(pos))
					.sort((a, b) =>
						b.quoteAssetAmount.abs().cmp(a.quoteAssetAmount.abs())
					)[0];
				if (biggestNotionalCrossPosition) {
					switchMarket({
						marketIndex: biggestNotionalCrossPosition.marketIndex,
						marketType: MarketType.PERP,
					});
				}
			}
		},
		selectedValue: selection,
	});

	// Get the label for the current selection
	const currentLabel = selectedOption?.label ?? 'Cross Positions';

	// Ensure selection remains valid based on filter and available options
	useEffect(() => {
		if (filteredMarginCalcs === 'cross') {
			// When filtering to cross, always use cross
			if (selection !== 'cross') {
				setSelection('cross');
			}
		} else if (filteredMarginCalcs === 'isolated') {
			// When filtering to isolated, ensure selection is an isolated position
			if (!selection.startsWith('iso-')) {
				// Set to first isolated position if available
				const firstIso = dropdownOptions.find((opt) =>
					opt.value.startsWith('iso-')
				);
				if (firstIso) {
					setSelection(firstIso.value as `iso-${number}`);
				}
			} else if (!dropdownOptions.some((opt) => opt.value === selection)) {
				// If selected isolated market no longer exists, reset to first available
				const firstIso = dropdownOptions.find((opt) =>
					opt.value.startsWith('iso-')
				);
				if (firstIso) {
					setSelection(firstIso.value as `iso-${number}`);
				}
			}
		} else {
			// No filter: keep existing behavior
			if (!hasMultipleOptions && selection !== 'cross') {
				setSelection('cross');
			}
			// If selected isolated market no longer exists, reset to cross
			if (
				hasMultipleOptions &&
				selection.startsWith('iso-') &&
				!dropdownOptions.some((opt) => opt.value === selection)
			) {
				setSelection('cross');
			}
		}
	}, [filteredMarginCalcs, hasMultipleOptions, selection, dropdownOptions]);

	// Auto-switch selection based on currently viewed market
	useEffect(() => {
		if (!marginCalc || !selectedMarket?.isPerp) return;

		const marketIndex = selectedMarket.marketIndex;
		const hasIsolatedPosition = marginCalc.isolatedMarginCalculations
			.get(marketIndex)
			?.marginRequirement.gt(ZERO);

		if (hasIsolatedPosition) {
			setSelection(`iso-${marketIndex}`);
		} else {
			setSelection('cross');
		}
	}, [selectedMarket]);

	// Derive numbers based on selection
	const { healthPercentFormatted, health, perpMarketIndex } =
		usePositionSummary(selection);

	const leverage = useMemo(() => {
		return marginInfo?.leverage ?? 0;
	}, [marginInfo?.leverage]);

	const totalCollateral = useMemo(() => {
		if (!marginCalc) return PRICE_FALLBACK_DISPLAY_VALUE;
		if (selection.startsWith('iso-')) {
			const iso = marginCalc.isolatedMarginCalculations.get(
				Number(selection.split('iso-')[1])
			);
			if (!iso) return PRICE_FALLBACK_DISPLAY_VALUE;
			return toNotional(
				BigNum.from(iso.totalCollateral, QUOTE_PRECISION_EXP).toNum()
			);
		}
		if (marginCalc.totalCollateral === undefined)
			return PRICE_FALLBACK_DISPLAY_VALUE;
		return toNotional(
			BigNum.from(marginCalc.totalCollateral, QUOTE_PRECISION_EXP).toNum()
		);
	}, [marginCalc, selection]);

	const marginRequirement = useMemo(() => {
		if (!marginCalc) return PRICE_FALLBACK_DISPLAY_VALUE;
		if (selection.startsWith('iso-')) {
			const iso = marginCalc.isolatedMarginCalculations.get(
				Number(selection.split('iso-')[1])
			);
			if (!iso) return PRICE_FALLBACK_DISPLAY_VALUE;
			return toNotional(
				BigNum.from(iso.marginRequirement, QUOTE_PRECISION_EXP).toNum()
			);
		}
		if (marginCalc.marginRequirement === undefined)
			return PRICE_FALLBACK_DISPLAY_VALUE;
		return toNotional(
			BigNum.from(marginCalc.marginRequirement, QUOTE_PRECISION_EXP).toNum()
		);
	}, [marginCalc, selection]);

	const isIsolated = useMemo(() => selection.startsWith('iso-'), [selection]);
	const selectedPerpPositionLeverage = useMemo(() => {
		if (!isIsolated) return null;
		const perpPosition = activePerpPositions?.find(
			(pos) => pos.marketIndex === perpMarketIndex
		);
		return convertMarginRatioToLeverage(perpPosition?.maxMarginRatio ?? 1);
	}, [userClient, isIsolated, selection, perpMarketIndex, activePerpPositions]);

	return (
		<div
			className={cj(
				'flex w-full flex-col gap-y-md bg-container-bg p-md',
				'rounded-sm',
				className
			)}
		>
			<div className="flex items-center gap-x-1">
				{hasMultipleOptions ? (
					<Select.Root
						open={open}
						onOpenChange={onOpenChange}
						value={selectedValue}
						onValueChange={onValueChange}
					>
						<Select.Trigger className="border-0 bg-transparent p-0 hover:bg-transparent">
							{currentLabel}
						</Select.Trigger>
						<Select.Options>
							{selectOptions.map((option) => {
								const marketIndex = option.value.startsWith('iso-')
									? Number(option.value.split('iso-')[1])
									: null;
								const isLowHealth =
									marketIndex !== null && lowHealthMarkets.has(marketIndex);
								return (
									<Select.Option key={option.value} value={option.value}>
										<span className="flex items-center gap-1">
											{option.label}
											{isLowHealth && (
												<WarningFilled
													size={12}
													color={getColorVar('star-yellow')}
												/>
											)}
										</span>
									</Select.Option>
								);
							})}
						</Select.Options>
					</Select.Root>
				) : (
					<span className="not-[]:typo-label-sm text-text-default">
						{currentLabel}
					</span>
				)}
			</div>

			<div className="flex w-full flex-col gap-y-sm typo-body-xs">
				<div className="flex w-full items-start justify-between">
					<span className="text-text-secondary">
						{isIsolated ? 'Position Health' : 'Account Health'}
					</span>
					<div className="flex items-center gap-1">
						<Health
							size={14}
							color={healthDependentClassName(health, {
								type: 'color-var',
							})}
						/>
						<span
							className={healthDependentClassName(health, {
								type: 'text',
							})}
						>
							{healthPercentFormatted}
						</span>
						{!isIsolated && (
							<IconButton
								size="sm"
								variant="tertiary"
								className="-m-sm p-sm"
								icon={Open}
								onClick={() => show({ type: 'account_health', props: {} })}
							/>
						)}
					</div>
				</div>

				<div className="flex w-full items-start justify-between">
					<DefinitionTooltip
						definitionId="tradingTotalCollateral"
						side="top"
						disableHoverableContent={false}
						className={cj('text-text-secondary')}
					>
						Total Collateral
					</DefinitionTooltip>
					<span className="text-text-default">{totalCollateral}</span>
				</div>

				<div className="flex w-full items-start justify-between">
					<DefinitionTooltip
						definitionId="tradingMaintenanceMargin"
						side="top"
						disableHoverableContent={false}
						className={cj(
							'text-text-secondary',
							TOOLTIP_TRIGGER_DECORATION_CLASSNAME
						)}
					>
						Maintenance Margin
					</DefinitionTooltip>
					<span className="text-text-default">{marginRequirement}</span>
				</div>
				<div className="flex w-full items-start justify-between">
					<span className="text-text-secondary">
						{isIsolated ? 'Position' : 'Account'} Leverage
					</span>
					<span className="text-text-default">
						{isIsolated
							? !selectedPerpPositionLeverage
								? PRICE_FALLBACK_DISPLAY_VALUE
								: `${selectedPerpPositionLeverage}x`
							: `${parseFloat(leverage.toFixed(2))}x`}
					</span>
				</div>
			</div>
			{includeDetailsAndDepositButtons && (
				<div className="mt-sm flex w-full gap-sm">
					{!isIsolated && (
						<Button
							size="sm"
							variant="secondary"
							className="w-1/2"
							onClick={() => show({ type: 'account_health', props: {} })}
						>
							Details
						</Button>
					)}
					<Button
						size="sm"
						className={isIsolated ? 'w-full' : 'w-1/2'}
						onClick={() => show({ type: 'deposit', props: {} })}
					>
						Deposit
					</Button>
				</div>
			)}
		</div>
	);
};

export default React.memo(MarginCalcPanel);
